<template>
  <div>
    <div class="mt-2 choices-list">
      <ospe-study-visit-choice-card
        v-model:choice="studyVisitApplication.choice"
        title="Study visit"
        @change-choice="showSelectExchangeModal"
        @remove-choice="showDeleteExchangePrompt"
      />
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <remote-modal
        ref="selectExchangeModal"
        modal-title="Select exchange"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        loading-message="Loading exchanges ..."
        :loading-indicator-in-footer="false"
        :hide-content-when-loading="true"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          v-if="selectFromExchangesListLoaded"
          v-slot:modal-content
        >
          <div
            v-if="selectFromExchangesList.length > 0"
            class="table-wrapper"
          >
            <table class="table table-hover table-sm study-visit-periods">
              <thead>
                <tr>
                  <th>Institution / period</th>
                  <th>Language</th>
                  <th>Available places</th>
                  <th class="tight" />
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="exchange of selectFromExchangesList"
                  :key="`sv-${exchange.id}`"
                >
                  <tr
                    class="table-secondary"
                  >
                    <td colspan="4"><strong>{{ exchange.institutionName }}</strong></td>
                  </tr>
                  <tr
                    v-for="period of exchange.periods"
                    :key="`svp-${period.id}`"
                    class="period"
                  >
                    <td class="dates">{{ period.dates }}</td>
                    <td>{{ period.language }}</td>
                    <td>{{ period.availablePlaces }}</td>
                    <td class="tight-right">
                      <button
                        type="button"
                        class="btn btn-sm btn-info"
                        @click.prevent="selectChoice(exchange, period)"
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="alert alert-warning"
          >
            No exchanges found for selected criteria
          </div>
        </template>
        <template v-slot:modal-footer>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </template>
      </remote-modal>
      <modal-alert
        ref="modalAlert"
        close-button-title="Close"
      />
      <modal-confirmation-rails
        ref="removeChoiceConfirmationModal"
        modal-title="Remove choice?"
        @confirmed="onRemoveChoiceConfirmed"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref, useTemplateRef, watch} from 'vue'

import Jsona from 'jsona'

import type {OspeActivityType} from '../../ospe-public/types'
import type {
  OspeStudyVisitApplication,
  OspeStudyVisitChoice,
  OspeStudyVisitPlace,
  OspeStudyVisitPlacePeriod
} from '../../ospe-types'
import ModalAlert from '../modal-alert.vue'
import ModalConfirmationRails from '../modal-confirmation-rails.vue'
import OspeStudyVisitChoiceCard from './ospe-study-visit-choice-card.vue'
import RemoteModal from '../remote-modal.vue'

interface ExchangeData {
  activityType: OspeActivityType;
}

const props = defineProps({
  availableChoicesUrl: {default: '', type: String},
})

// Template refs
const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const removeChoiceConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('removeChoiceConfirmationModal')

// Reactive state
const studyVisitApplication = ref<OspeStudyVisitApplication>({choice: null})
const activityType = ref<OspeActivityType>({
  id: 0,
  name: '',
  requiresSpecialisation: false,
  functions: null,
  specialisations: null,
  courtStaffWorkplaces: null,
  otherCountryAllocation: false,
})
const selectFromExchangesList = ref<OspeStudyVisitPlace[]>([])
const selectFromExchangesListLoaded = ref(false)
const applicationDataEl = ref<HTMLInputElement | null>(null)

// Watch
watch(studyVisitApplication, () => {
  storeApplicationData()
}, {deep: true})

// Lifecycle
onMounted(() => {
  const exchangesDataEl = document.querySelector('script[ref="exchangeSelectionData"]')
  if (exchangesDataEl instanceof HTMLScriptElement && exchangesDataEl.type == 'application/json') {
    try {
      const exchangeData: ExchangeData = JSON.parse(exchangesDataEl.innerText)
      if (exchangeData.activityType) {
        activityType.value = exchangeData.activityType
      }
    } catch { /* empty */
    }
  }

  const dataEl = document.querySelector('input[ref="applicationDataJson"]')
  if (dataEl instanceof HTMLInputElement) {
    applicationDataEl.value = dataEl
    try {
      const appData = JSON.parse(applicationDataEl.value.value)
      studyVisitApplication.value.choice = appData.choice ?? null
    } catch { /* empty */
    }
  }
})

// Methods
const showSelectExchangeModal = (): void => {
  if (activityType.value.id == 0) {
    // no activity type
    return
  }

  selectFromExchangesListLoaded.value = false

  selectExchangeModal.value?.showModal(props.availableChoicesUrl, {
    activity_type_id: activityType.value.id,
  })
}

const showDeleteExchangePrompt = (): void => {
  removeChoiceConfirmationModal.value?.show(null, {
    title: 'Remove choice?',
    message: 'Are you sure you want to remove this choice?',
    type: 'danger',
    cancelActionTitle: 'Cancel',
    confirmActionTitle: 'Remove',
    confirmationCode: null,
  })
}

const onSelectExchangesLoaded = (data: any): void => {
  selectFromExchangesListLoaded.value = true
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectFromExchangesList.value = loadedData as OspeStudyVisitPlace[]
  }
}

const prepareChoice = (place: OspeStudyVisitPlace, period: OspeStudyVisitPlacePeriod, previousChoice: OspeStudyVisitChoice | null): OspeStudyVisitChoice => {
  // copy period
  period = JSON.parse(JSON.stringify(period))
  const choice: OspeStudyVisitChoice = {
    id: place.id,
    institutionName: place.institutionName,
    activityPlacePeriodId: period.id,
    activityPlacePeriodDate: period.dates,
    activityPlacePeriodLanguage: period.language,
    specialInterest: '',
    errorMessages: [],
  }

  if (previousChoice) {
    choice.specialInterest = previousChoice.specialInterest
  }
  return choice
}

const selectChoice = (place: OspeStudyVisitPlace, choice: OspeStudyVisitPlacePeriod | null): void => {
  selectExchangeModal.value?.hideModal()

  if (choice) {
    studyVisitApplication.value.choice = prepareChoice(place, choice, studyVisitApplication.value.choice)
  } else {
    studyVisitApplication.value.choice = null
  }
}

const onRemoveChoiceConfirmed = (): void => {
  removeChoiceConfirmationModal.value?.hide()
  studyVisitApplication.value.choice = null
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(studyVisitApplication.value)
  }
}
</script>
